import React, { useCallback, useEffect } from 'react';

import {
  adminActions,
  adminGetAllNotes,
  adminGetAllPharmacyNotes,
  ConfirmationTypes,
  getPharmacistDetails,
  getPharmacyDetails,
  HelpTypes,
  resetStatus,
  sendPharmaPlanInvitation,
  ServiceTypes,
  successSelector,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../../localization';
import { resetDialog, setDialog } from '../../../reducers/dialogReducer';
import useAdmin from '../../useAdmin';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';

interface IUsePharmaPlanInvitation {
  personnelId: string;
  userType: HelpTypes;
  canSendInvite: boolean;
  canResumePorting: boolean;
  lastPPInviteSentAt: string | null;
}

const invitationAction = adminActions.sendPharmaPlanInvitation;

const getInvitationLabel = (
  canResumePorting: boolean,
  lastPPInviteSentAt: string | null,
) => {
  if (canResumePorting) {
    return strings.resumePortingToPharmaPlan;
  }

  return lastPPInviteSentAt
    ? strings.reinviteToPharmaPlan
    : strings.inviteToPharmaPlan;
};

const usePharmaPlanInvitation = ({
  personnelId,
  userType,
  canSendInvite,
  canResumePorting,
  lastPPInviteSentAt,
}: IUsePharmaPlanInvitation) => {
  const dispatch = useAppDispatch();
  const { isSuperAdmin } = useAdmin();

  const isSuccess = useAppSelector((state) =>
    successSelector([invitationAction], state));

  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
  const canInvite = isSelfService
    && isSuperAdmin
    && !!personnelId
    && canSendInvite;

  const refreshProfile = useCallback(() => {
    if (userType === HelpTypes.pharmacy) {
      dispatch(getPharmacyDetails(personnelId));
      dispatch(adminGetAllPharmacyNotes(personnelId));
      return;
    }

    dispatch(getPharmacistDetails(personnelId));
    dispatch(adminGetAllNotes(personnelId));
  }, [dispatch, personnelId, userType]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    dispatch(resetDialog());
    showSuccess(dispatch, strings.actionCompletedSuccessfully);
    refreshProfile();
    dispatch(resetStatus([invitationAction]));
  }, [dispatch, isSuccess, refreshProfile]);

  useEffect(() =>
    () => {
      dispatch(resetStatus([invitationAction]));
    }, [dispatch]);

  const handleSend = () => {
    dispatch(sendPharmaPlanInvitation({ personnelId }));
  };

  const handleInvite = () => {
    dispatch(
      setDialog({
        showCloseButton: false,
        heading: { title: '' },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.pharmaPlanInvitation}
            confirmAction={handleSend}
          />
        ),
      }),
    );
  };

  const action = canInvite
    ? {
      key: 'pharmaPlanInvitation',
      label: getInvitationLabel(canResumePorting, lastPPInviteSentAt),
      onClick: handleInvite,
    }
    : null;

  return { action };
};

export default usePharmaPlanInvitation;
